// components/highlight-toolbar.js — TEXT SELECTION CAPTURE TOOLBAR
// ═══════════════════════════════════════════════════════════
// Pops above any text selection inside a message. Four actions:
//   Highlight → new note, type='highlight', keeps source_text
//   Ideas     → new note, type='idea'
//   Notes     → new note, type='note'
//   Reply     → quote the selection into the chat input (parent
//               handles via onReply)
//
// Message ID is extracted from the nearest DOM ancestor carrying
// data-msg-id (every chat message element has this attribute —
// see components/chat-surface/chat-message-list.js and features/
// chat-feature.js). This keeps the toolbar agnostic to the chat
// surface hosting it.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNotes } from '../hooks/use-notes';

export default function HighlightToolbar({
  userId,
  supabase,
  sessionId,
  onSaved,
  onReply,
}) {
  const [visible, setVisible] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [messageId, setMessageId] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [saving, setSaving] = useState(false);
  const toolbarRef = useRef(null);
  const justDismissedRef = useRef(false);
  const { createNote } = useNotes({ userId, supabase });

  const handleMouseUp = useCallback(() => {
    setTimeout(() => {
      if (justDismissedRef.current) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !sel.toString().trim()) {
        setVisible(false);
        return;
      }
      const text = sel.toString().trim();
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Walk up from the selection's start to the nearest element that
      // carries a data-msg-id attribute. That's the chat message the
      // user selected inside — the birth message for this note.
      const startNode = range.startContainer;
      const startEl = startNode.nodeType === 3 ? startNode.parentElement : startNode;
      const msgEl = startEl?.closest?.('[data-msg-id]') || null;
      const msgId = msgEl?.getAttribute('data-msg-id') || null;

      setSelectedText(text);
      setMessageId(msgId);
      setPosition({
        top: rect.top + window.scrollY - 48,
        left: Math.max(8, rect.left + window.scrollX),
      });
      setVisible(true);
    }, 10);
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (toolbarRef.current?.contains(e.target)) return;
    justDismissedRef.current = true;
    setVisible(false);
    setTimeout(() => { justDismissedRef.current = false; }, 200);
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [handleMouseUp, handleMouseDown]);

  async function save(noteType) {
    if (!selectedText || saving) return;
    setSaving(true);
    try {
      const payload = {
        note_type: noteType,
        content: selectedText,
        session_id: sessionId || null,
        message_id: messageId || null,
      };
      // For highlights, mirror content into source_text so the
      // original snippet survives if the user later edits content.
      if (noteType === 'highlight') payload.source_text = selectedText;
      const note = await createNote(payload);
      onSaved?.(note);
    } catch (e) {
      console.error('HighlightToolbar save:', e);
    } finally {
      setSaving(false);
      setVisible(false);
      window.getSelection()?.removeAllRanges();
    }
  }

  function handleReply() {
    if (!selectedText) return;
    onReply?.(selectedText);
    setVisible(false);
    window.getSelection()?.removeAllRanges();
  }

  function dismiss() {
    setVisible(false);
    window.getSelection()?.removeAllRanges();
  }

  if (!visible) return null;

  const BTN_STYLE = {
    background: 'none', border: 'none',
    color: '#666', fontSize: 11,
    fontFamily: 'Georgia, serif',
    padding: '3px 7px', borderRadius: 4, cursor: 'pointer',
  };

  return (
    <div
      ref={toolbarRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 9999,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        gap: 4, pointerEvents: 'all',
      }}
    >
      <div style={{
        background: '#1e1e2a',
        border: '1px solid #2a2a2a',
        borderRadius: 8,
        padding: '5px 10px',
        display: 'flex', alignItems: 'center', gap: 2,
        fontFamily: 'Georgia, serif',
      }}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); save('highlight'); }}
          style={BTN_STYLE}
          onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
          onMouseLeave={e => e.currentTarget.style.color = '#666'}
        >
          Highlight
        </button>

        <div style={{ width: 0.5, height: 16, background: '#2a2a2a', margin: '0 4px' }} />

        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); save('idea'); }}
          style={BTN_STYLE}
          onMouseEnter={e => e.currentTarget.style.color = '#9b8fc4'}
          onMouseLeave={e => e.currentTarget.style.color = '#666'}
        >
          Ideas
        </button>

        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); save('note'); }}
          style={BTN_STYLE}
          onMouseEnter={e => e.currentTarget.style.color = '#c8a96e'}
          onMouseLeave={e => e.currentTarget.style.color = '#666'}
        >
          Notes
        </button>

        {typeof onReply === 'function' && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleReply(); }}
            style={BTN_STYLE}
            onMouseEnter={e => e.currentTarget.style.color = '#7a9e82'}
            onMouseLeave={e => e.currentTarget.style.color = '#666'}
          >
            Reply
          </button>
        )}

        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(); }}
          style={{
            background: 'none', border: 'none',
            color: '#333', fontSize: 13,
            padding: '2px 4px', marginLeft: 2,
            cursor: 'pointer', lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>

      <div style={{
        width: 0, height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '5px solid #2a2a2a',
        marginLeft: 12,
      }} />
    </div>
  );
}
